/**
 * What a source column may become.
 *
 * Two rules, and both exist because a mapping that is merely *possible* is not
 * the same as one that will work:
 *
 *   **A property takes one column.** Two columns pointing at `account_name`
 *   means one of them wins silently and the other is lost — a data-loss bug
 *   that looks like a configuration choice. So a property already claimed
 *   elsewhere is not offered again.
 *
 *   **The types have to agree.** Putting a date column into a number property
 *   produces a record that fails its own quality rules on every row, and the
 *   steward finds out after the import rather than during the mapping.
 *
 * Detection is a guess made from three sample rows, so `unknown` — a column
 * that was empty in all of them — is compatible with everything. Guessing
 * wrong in the strict direction would block a mapping that is actually fine.
 */

/** Property types a column of each detected type can honestly fill. */
const COMPATIBLE: Record<string, string[]> = {
  // Text goes anywhere text-shaped. `enum` is included because its values are
  // strings; the quality rule on the enum is what checks they are the right
  // ones, which is a better place for that check than here.
  string: ['string', 'text', 'email', 'phone', 'url', 'enum'],
  email: ['email', 'string', 'text'],
  integer: ['integer', 'number', 'string', 'text'],
  // A decimal into an integer property is a silent truncation, so it is not
  // offered; the other direction is safe.
  number: ['number', 'string', 'text'],
  boolean: ['boolean', 'string', 'text'],
  date: ['date', 'datetime', 'string', 'text'],
  array: ['array', 'json'],
  json: ['json', 'text'],
}

/** Whether a column of this detected type can fill a property of that type. */
export function typesAgree(detected: string, propertyType: string): boolean {
  // Nothing was sampled, so nothing can be ruled out.
  if (!detected || detected === 'unknown') return true
  const allowed = COMPATIBLE[detected]
  // A detected type we have no opinion about is not a reason to block anyone.
  if (!allowed) return true
  return allowed.includes(propertyType)
}

/** Why a property is not on offer, in words a person can act on. */
export function whyUnavailable(
  detected: string,
  propertyType: string,
  claimedBy: string | undefined,
): string | null {
  if (claimedBy) return `already mapped from ${claimedBy}`
  if (!typesAgree(detected, propertyType)) return `${detected} does not fit ${propertyType}`
  return null
}
